/**
 * @file useSectionDraft.ts
 * @description Форма секции v2 в панели конструктора (phase19.16 Р2;
 * uiux-design-r2 §9, §10; контракт §16.2, §16.6; решения 2026-10-09:
 * черновика страниц нет — «Сохранить блок» остаётся, канва показывает
 * несохранённые правки сразу).
 *
 *  - правки держатся в форме, `dirty` — сравнение с сохранённым блоком;
 *    набранное во время сохранения не сбрасывается приходом нового блока;
 *  - проверка `validateSectionPayload` (shared, русские сообщения) идёт
 *    сразу, сохранить с замечаниями нельзя; ответ 422 дополняет замечания;
 *  - сохранение — `save` вызывающего (с `expectedUpdatedAt`); 409
 *    `block_changed` — текст и `onConflict` (страница перечитывается, окно
 *    «Блок изменили» решает, чьи правки оставить);
 *  - предупреждения о текстах — из ответа сохранения и `check-texts` через
 *    секунду после правки (устаревший ответ отбрасывается);
 *  - канва получает `block-draft` (без записи на сайт); уход без
 *    сохранения возвращает канве сохранённый вид (`payload: null`);
 *    SB120 этап 3а: вместе с черновиком — `shopFonts` (свои шрифты
 *    элементов, когда список своих шрифтов магазина получен).
 *
 * Р2а: проверка — в контексте секции (`context`: в шаблоне товара
 * разрешены элементы данных товара); блок шаблона сохраняется в черновик
 * шаблона — на сайте после публикации вида, о чём и говорит всплывашка.
 *
 * SB122 (ревью партии): `failedSaves` — сколько раз «Сохранить» не прошло
 * из-за замечаний (проверка или 422); по нему панель раскрывает первую
 * группу с ошибкой и даёт фокус полю.
 */

import { validateSectionPayload, type SectionContext, type SectionPayload } from "@chaiberi/shared";
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";

import { ApiError } from "../../lib/api-client";
import { sitePages, type BlockAppearance, type SavedSiteBlock, type SitePageBlock, type SiteTextWarning } from "../../lib/api-site-pages";
import { blockErrorText, payloadIssues } from "../../lib/site-builder/block-errors";
import { useCanvasFontRegistry } from "../../components/site-view/shop-fonts-context";
import { blockDraftMessage, postToCanvas, sectionShopFonts } from "../../lib/site-view/canvas-messages";

export interface SectionDraft {
  payload: SectionPayload;
  setPayload: (payload: SectionPayload) => void;
  appearance: BlockAppearance;
  setAppearance: (appearance: BlockAppearance) => void;
  dirty: boolean;
  saving: boolean;
  issues: Array<{ path: string; message: string }>;
  warnings: SiteTextWarning[];
  /** Неудачных сохранений из-за замечаний (растёт с каждой попыткой). */
  failedSaves: number;
  /** Сохранить; `true` — сохранено. */
  save: () => Promise<boolean>;
  reset: () => void;
}

interface Deps {
  block: SitePageBlock;
  iframeRef: RefObject<HTMLIFrameElement | null>;
  notify: { success: (text: string) => void; error: (text: string) => void };
  /** Запись блока (с `expectedUpdatedAt` и историей «Отменить»). */
  save: (block: SitePageBlock, next: { payload: SectionPayload; appearance: BlockAppearance }) => Promise<SavedSiteBlock>;
  onSaved: () => void;
  /** 409 `block_changed` — перечитать страницу. */
  onConflict: () => void;
  /** Где секция (какие элементы разрешены); по умолчанию — обычная страница. */
  context?: SectionContext;
}

/** Всплывашка после сохранения: Р3а — блоки страниц и мест тоже в черновике (SB126). */
export const SAVED_TEXT: Readonly<Record<SectionContext, string>> = {
  page: "Блок сохранён в черновик страницы — на сайте после «Опубликовать»",
  slot: "Блок сохранён в черновик — на сайте после «Опубликовать»",
  productTemplate: "Блок сохранён в черновик шаблона — на сайте после «Опубликовать»",
};

export const LIVE_DELAY_MS = 150;
export const CHECK_DELAY_MS = 1000;

export function useSectionDraft({ block, iframeRef, notify, save: write, onSaved, onConflict, context = "page" }: Deps): SectionDraft {
  const saved = block.payload as SectionPayload;
  const [payload, setPayloadState] = useState<SectionPayload>(() => structuredClone(saved));
  const [appearance, setAppearanceState] = useState<BlockAppearance>(() => structuredClone(block.appearance) ?? {});
  const [saving, setSaving] = useState(false);
  const [serverIssues, setServerIssues] = useState<Array<{ path: string; message: string }>>([]);
  const [warnings, setWarnings] = useState<SiteTextWarning[]>([]);
  const [failedSaves, setFailedSaves] = useState(0);
  // Правка во время сохранения: новый блок с сервера её не затирает.
  const savingRef = useRef(false);
  const typedDuringSave = useRef(false);
  const setPayload = useCallback((next: SectionPayload) => {
    if (savingRef.current) typedDuringSave.current = true;
    setPayloadState(next);
  }, []);
  const setAppearance = useCallback((next: BlockAppearance) => {
    if (savingRef.current) typedDuringSave.current = true;
    setAppearanceState(next);
  }, []);

  // Новая версия блока с сервера (сохранили, правка на канве, приняли чужую) — форма заново.
  useEffect(() => {
    if (typedDuringSave.current) {
      typedDuringSave.current = false;
      return;
    }
    setPayloadState(structuredClone(block.payload as SectionPayload));
    setAppearanceState(structuredClone(block.appearance) ?? {});
    setServerIssues([]);
  }, [block.id, block.payload, block.appearance]);

  const dirty = JSON.stringify(payload) !== JSON.stringify(saved) || JSON.stringify(appearance) !== JSON.stringify(block.appearance ?? {});
  const validation = useMemo(() => validateSectionPayload(payload, { context }), [context, payload]);
  const issues = useMemo(() => [...(validation.ok ? [] : validation.issues), ...serverIssues], [validation, serverIssues]);

  // Живой предпросмотр на канве: только пока есть правки; свои шрифты элементов — при полученном списке.
  const fontRegistry = useCanvasFontRegistry();
  const wasDirty = useRef(false);
  useEffect(() => {
    if (!dirty && !wasDirty.current) return;
    wasDirty.current = dirty;
    const shopFonts = dirty && fontRegistry ? sectionShopFonts(payload, fontRegistry) : undefined;
    const t = setTimeout(() => postToCanvas(iframeRef.current, blockDraftMessage(block.id, dirty ? payload : null, appearance, shopFonts)), LIVE_DELAY_MS);
    return () => clearTimeout(t);
  }, [appearance, block.id, dirty, fontRegistry, iframeRef, payload]);
  // Ушли из панели с несохранёнными правками — канва возвращает сохранённое.
  const latestDirty = useRef(dirty);
  latestDirty.current = dirty;
  useEffect(
    () => () => {
      if (latestDirty.current) postToCanvas(iframeRef.current, blockDraftMessage(block.id, null, null));
    },
    [block.id, iframeRef],
  );

  // Предупреждения о текстах — после паузы в наборе; ответ на прежний текст отбрасывается.
  useEffect(() => {
    if (!dirty || !validation.ok) return;
    let alive = true;
    const t = setTimeout(() => {
      sitePages
        .checkTexts(validation.payload)
        .then((res) => alive && setWarnings(res.warnings))
        .catch(() => undefined); // Проверка — подсказка; без неё сохранить можно.
    }, CHECK_DELAY_MS);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [dirty, validation]);

  const save = useCallback(async () => {
    if (!validation.ok) {
      notify.error(`Не сохранилось: ${validation.issues[0]?.message ?? "проверьте поля"}`);
      setFailedSaves((n) => n + 1);
      return false;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const res = await write(block, { payload: validation.payload, appearance });
      setWarnings(res.textWarnings ?? []);
      setServerIssues([]);
      notify.success(SAVED_TEXT[context]);
      onSaved();
      return true;
    } catch (err) {
      typedDuringSave.current = false;
      // 409: одно поведение — окно у панели (правки остаются), без всплывашки.
      if (err instanceof ApiError && err.code === "block_changed") {
        onConflict();
        return false;
      }
      const found = payloadIssues(err);
      setServerIssues(found);
      if (found.length > 0) setFailedSaves((n) => n + 1);
      notify.error(found[0] ? `Не сохранилось: ${found[0].message}` : blockErrorText(err, "Не удалось сохранить блок."));
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [appearance, block, context, notify, onConflict, onSaved, validation, write]);

  const reset = useCallback(() => {
    setPayloadState(structuredClone(block.payload as SectionPayload));
    setAppearanceState(structuredClone(block.appearance) ?? {});
    setServerIssues([]);
    setWarnings([]);
  }, [block.appearance, block.payload]);

  return { payload, setPayload, appearance, setAppearance, dirty, saving, issues, warnings, failedSaves, save, reset };
}
